import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Logo from './Logo';

// Nav items. Items with `children` render a dropdown on hover / focus.
const nav = [
    {
        label: 'Shop',
        to: '/shop',
        children: [
            { to: '/shop', label: 'All Equipment' },
            { to: '/shop?type=machine', label: 'Dry Ice Machines' },
            { to: '/shop?type=accessory', label: 'Accessories & Parts' },
            { to: '/shop?category=used', label: 'Used Equipment' },
        ],
    },
    { label: 'Rental', to: '/rental', highlight: true },
    { label: 'Training', to: '/training' },
    {
        label: 'Partners',
        to: '/partners',
        children: [
            { to: '/partners', label: 'Partner Program' },
            { to: '/partners/apply', label: 'Become a Partner' },
            { to: '/partner-locations', label: 'Partner Locations Map' },
        ],
    },
    {
        label: 'Support',
        to: '/services',
        children: [
            { to: '/services', label: 'Service & Support' },
            { to: '/projects', label: 'Projects' },
            { to: '/industries', label: 'Industries' },
            { to: '/machine-finder', label: 'Machine Finder' },
            { to: '/knowledge', label: 'Knowledge Center' },
            { to: '/videos', label: 'Videos' },
            { to: '/faq', label: 'FAQ' },
        ],
    },
    {
        label: 'Company',
        to: '/about',
        children: [
            { to: '/about', label: "About & Ken's Story" },
            { to: '/news', label: 'News' },
            { to: '/careers', label: 'Careers' },
            { to: '/contact', label: 'Contact' },
        ],
    },
];

const CartLink = ({ testid }) => {
    const { count } = useCart();
    return (
        <Link to="/cart" className="relative p-2 text-neutral-800 hover:text-brand-primary transition-colors" aria-label="cart" data-testid={testid}>
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid={`${testid}-count`}>
                    {count}
                </span>
            )}
        </Link>
    );
};

const DesktopItem = ({ item }) => {
    const [open, setOpen] = useState(false);

    if (!item.children) {
        return (
            <NavLink
                to={item.to}
                className={({ isActive }) =>
                    item.highlight
                        ? `mx-1 px-3 py-1.5 label-caps border transition-colors ${isActive ? 'bg-brand-primary border-brand-primary text-white' : 'border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white'}`
                        : `px-3 xl:px-4 py-2 label-caps transition-colors ${
                        isActive ? 'text-brand-primary' : 'text-neutral-700 hover:text-neutral-900'
                    }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
            >
                {item.label}
            </NavLink>
        );
    }

    return (
        <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
        >
            <NavLink
                to={item.to}
                className={({ isActive }) =>
                    `flex items-center gap-1 px-3 xl:px-4 py-2 label-caps transition-colors ${
                        isActive ? 'text-brand-primary' : 'text-neutral-700 hover:text-neutral-900'
                    }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
            >
                {item.label}
                <ChevronDown className="h-3.5 w-3.5" />
            </NavLink>
            {open && (
                <div
                    className="absolute left-0 top-full pt-1 min-w-[240px] z-40"
                    data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                >
                    <div className="bg-white border border-neutral-200 shadow-xl py-2">
                        {item.children.map((c) => (
                            <Link
                                key={c.to}
                                to={c.to}
                                className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-brand-primary/10 hover:text-brand-primary transition-colors"
                                data-testid={`nav-dropdown-item-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {c.label}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Hide site header on admin routes (they have their own shell)
    if (location.pathname.startsWith('/admin')) return null;

    return (
        <header
            className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-neutral-200"
            data-testid="site-header"
        >
            <div className="container-page flex items-center justify-between h-24">
                <Link to="/" data-testid="header-logo-link">
                    <Logo size="md" plain />
                </Link>

                <nav className="hidden lg:flex items-center gap-0">
                    <NavLink
                        to="/"
                        end
                        className={({ isActive }) =>
                            `px-3 xl:px-4 py-2 label-caps transition-colors ${
                                isActive ? 'text-brand-primary' : 'text-neutral-700 hover:text-neutral-900'
                            }`
                        }
                        data-testid="nav-home"
                    >
                        Home
                    </NavLink>
                    {nav.map((item) => (
                        <DesktopItem key={item.label} item={item} />
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href="tel:+19842010767"
                        onClick={() => window.trackConversion && window.trackConversion('phone_click', { location: 'header' })}
                        className="flex items-center gap-2 text-sm text-neutral-700 hover:text-brand-primary transition-colors whitespace-nowrap"
                        data-testid="header-phone-link"
                    >
                        <Phone className="h-4 w-4" />
                        (984) 201-0767
                    </a>
                    <CartLink testid="header-cart-link" />
                    <Link
                        to="/contact?interest=quote&subject=Equipment+Quote"
                        onClick={() => window.trackConversion && window.trackConversion('quote_click', { location: 'header' })}
                        className="bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-5 py-2.5 label-caps transition-colors whitespace-nowrap"
                        data-testid="header-cta-quote"
                    >
                        Get a Quote
                    </Link>
                </div>

                <div className="lg:hidden flex items-center gap-1">
                    <CartLink testid="mobile-cart-link" />
                    <button
                        className="p-2 text-neutral-900"
                        onClick={() => setOpen(!open)}
                        aria-label="menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
                    <div className="container-page py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
                        <NavLink
                            to="/"
                            end
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `px-4 py-3 label-caps transition-colors ${
                                    isActive ? 'text-brand-primary' : 'text-neutral-800'
                                }`
                            }
                            data-testid="mobile-nav-home"
                        >
                            Home
                        </NavLink>
                        {nav.map((item) => (
                            <div key={item.label} className="border-t border-neutral-100">
                                <NavLink
                                    to={item.to}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `block px-4 py-3 label-caps transition-colors ${
                                            isActive || item.highlight ? 'text-brand-primary' : 'text-neutral-800'
                                        }`
                                    }
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                                {item.children && (
                                    <div className="pl-4 pb-2">
                                        {item.children.map((c) => (
                                            <Link
                                                key={c.to}
                                                to={c.to}
                                                onClick={() => setOpen(false)}
                                                className="block px-4 py-2 text-sm text-neutral-600 hover:text-brand-primary"
                                            >
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link
                            to="/contact?interest=quote&subject=Equipment+Quote"
                            onClick={() => setOpen(false)}
                            className="mt-3 bg-brand-primary text-white font-bold text-center px-5 py-3 label-caps"
                            data-testid="mobile-cta-quote"
                        >
                            Get a Quote
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
